import { Application, Container, TextureSource } from 'pixi.js';
import type { Agent } from '@/types/agent';
import type { Room } from '@/types/room';
import { FloorMapRenderer } from './FloorMapRenderer';
import { AgentSprite } from './AgentSprite';
import { bus } from '@/store/eventBus';

export class PixiApp {
  app?: Application;
  private floorMap?: FloorMapRenderer;
  private agentLayer?: Container;
  private agentSprites: Map<string, AgentSprite> = new Map();
  private destroyed = false;

  async init(container: HTMLElement, rooms: Room[], agents: Agent[]) {
    // Pixel-art : désactiver l'antialiasing sur toutes les textures
    TextureSource.defaultOptions.scaleMode = 'nearest';

    this.app = new Application();
    await this.app.init({
      width: 920,
      height: 420,
      background: 0x060910,
      antialias: false,
      resolution: 1,
      autoDensity: false,
    });

    // Si destroy() a été appelé pendant l'await, on s'arrête proprement
    if (this.destroyed) {
      this.app.destroy(true);
      return this;
    }

    // Injecter le canvas dans le DOM
    (this.app.canvas as HTMLCanvasElement).style.display = 'block';
    (this.app.canvas as HTMLCanvasElement).style.imageRendering = 'pixelated';
    container.appendChild(this.app.canvas);

    // ── Calques ─────────────────────────────────────────────────────────
    this.floorMap = new FloorMapRenderer(this.app);
    this.floorMap.render(rooms);

    this.agentLayer = new Container();
    this.app!.stage.addChild(this.agentLayer);

    // ── Agents initiaux ─────────────────────────────────────────────────
    for (const agent of agents) {
      this.addAgent(agent);
    }

    // ── Boucle de rendu 60fps ───────────────────────────────────────────
    this.app!.ticker.add((ticker) => {
      if (this.destroyed) return;
      this.agentSprites.forEach(sprite => {
        sprite.tick(ticker.deltaMS);
      });
    });

    return this;
  }

  addAgent(agent: Agent) {
    if (!this.agentLayer) return;
    const sprite = new AgentSprite(agent);
    sprite.setPosition(agent.position.x, agent.position.y);

    sprite.container.on('pointerdown', () => {
      bus.emit('agent:click', { agentId: agent.id });
    });

    this.agentLayer.addChild(sprite.container);
    this.agentSprites.set(agent.id, sprite);
  }

  /** Appelé depuis React quand le state Zustand change */
  syncAgents(agents: Agent[]) {
    if (!this.agentLayer) return;
    for (const agent of agents) {
      const sprite = this.agentSprites.get(agent.id);
      if (sprite) {
        sprite.syncState(agent);
      } else {
        this.addAgent(agent);
      }
    }
    // Supprimer les sprites d'agents disparus
    const currentIds = new Set(agents.map(a => a.id));
    this.agentSprites.forEach((sprite, id) => {
      if (!currentIds.has(id)) {
        sprite.destroy();
        this.agentSprites.delete(id);
      }
    });
  }

  refreshFloorMap(rooms: Room[]) {
    this.floorMap?.render(rooms);
  }

  destroy() {
    this.destroyed = true;
    // Pixi cleanup — wrappé en try/catch car destroy() peut être appelé
    // avant que init() async soit terminé (React 18 Strict Mode)
    try {
      this.agentSprites.forEach(s => { try { s.destroy(); } catch { /* noop */ } });
      this.agentSprites.clear();
      this.floorMap?.destroy();
      // Pas d'arguments — évite le bug _cancelResize de PixiJS v8 quand
      // destroy() est appelé sur une Application partiellement initialisée
      this.app?.destroy();
    } catch (e) {
      // Ignoré : cleanup de Strict Mode sur une instance non-initialisée
    }
  }
}
